import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { usersAPI, distributionListAPI } from '../services/api';

const initialUserForm = {
  name: '',
  email: '',
  password: '',
  role: 'Visualizador',
  isDistributionList: false
};

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(initialUserForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        isDistributionList: user.isDistributionList || false
      });
    } else {
      setEditingUser(null);
      setFormData(initialUserForm);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(initialUserForm);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await usersAPI.update(editingUser._id, updateData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await usersAPI.create(formData);
        toast.success('Usuário criado com sucesso');
      }
      handleCloseModal();
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.message || 
                      error.response?.data?.errors?.[0]?.msg ||
                      'Erro ao salvar usuário';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersAPI.update(user._id, { active: !user.active });
      toast.success(user.active ? 'Usuário desativado' : 'Usuário ativado');
      loadUsers();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleToggleDistributionList = async (user) => {
    try {
      await distributionListAPI.update(user._id, !user.isDistributionList);
      toast.success(
        user.isDistributionList 
          ? 'Usuário removido da lista de distribuição' 
          : 'Usuário adicionado à lista de distribuição'
      );
      loadUsers();
    } catch (error) {
      toast.error('Erro ao atualizar lista de distribuição');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Deseja realmente excluir o usuário "${user.name}"?`)) {
      return;
    }

    try {
      await usersAPI.delete(user._id);
      toast.success('Usuário excluído com sucesso');
      loadUsers();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao excluir usuário';
      toast.error(message);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'Administrador': 'danger',
      'Financeiro': 'success',
      'Analista': 'info',
      'Operador': 'warning',
      'Visualizador': 'secondary'
    };
    return colors[role] || 'secondary';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuários</h1>
          <p className="page-subtitle">Gerencie os usuários e permissões do sistema</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary">
          ➕ Novo Usuário
        </button>
      </div>

      {/* Info Card */}
      <div className="card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '32px' }}>📧</span>
          <div>
            <h4 style={{ margin: 0, color: 'var(--gray-800)' }}>Lista de Distribuição</h4>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--gray-600)' }}>
              Usuários marcados com 📧 receberão e-mail sempre que um novo imóvel for cadastrado.
            </p>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : users.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Perfil</th>
                    <th>Status</th>
                    <th>Lista Dist.</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <strong>{user.name}</strong>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge badge-${getRoleColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.active ? 'badge-success' : 'badge-secondary'}`}>
                          {user.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleToggleDistributionList(user)}
                          className="btn btn-sm btn-icon"
                          style={{ 
                            background: user.isDistributionList ? 'var(--primary)' : 'var(--gray-200)',
                            color: user.isDistributionList ? 'white' : 'var(--gray-600)'
                          }}
                          title={user.isDistributionList ? 'Remover da lista' : 'Adicionar à lista'}
                        >
                          📧
                        </button>
                      </td>
                      <td>
                        {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => handleOpenModal(user)}
                            className="btn btn-sm btn-outline"
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`btn btn-sm ${user.active ? 'btn-secondary' : 'btn-success'}`}
                            title={user.active ? 'Desativar' : 'Ativar'}
                          >
                            {user.active ? '🚫' : '✅'}
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            className="btn btn-sm btn-danger"
                            title="Excluir"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <div className="empty-state-title">Nenhum usuário encontrado</div>
            </div>
          )}
        </div>
      </div>

      {/* Roles Legend */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">📋 Níveis de Permissão</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div>
              <span className="badge badge-danger">Administrador</span>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px' }}>
                Acesso total ao sistema, gerenciamento de usuários e configurações
              </p>
            </div>
            <div>
              <span className="badge badge-success">Financeiro</span>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px' }}>
                Acesso a dados financeiros e relatórios de valores
              </p>
            </div>
            <div>
              <span className="badge badge-info">Analista</span>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px' }}>
                Pode analisar e aprovar/rejeitar cadastros de imóveis
              </p>
            </div>
            <div>
              <span className="badge badge-warning">Operador</span>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px' }}>
                Pode cadastrar e editar proprietários e imóveis
              </p>
            </div>
            <div>
              <span className="badge badge-secondary">Visualizador</span>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginTop: '8px' }}>
                Apenas visualização de dados cadastrados
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label required">Nome</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={editingUser ? {} : { className: 'required' }}>
                    Senha {editingUser && '(deixe em branco para manter)'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    className="form-input"
                    placeholder={editingUser ? '••••••••' : 'Mínimo 6 caracteres'}
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    minLength={editingUser ? 0 : 6}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Perfil</label>
                  <select
                    name="role"
                    className="form-select"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="Administrador">Administrador</option>
                    <option value="Financeiro">Financeiro</option>
                    <option value="Analista">Analista</option>
                    <option value="Operador">Operador</option>
                    <option value="Visualizador">Visualizador</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-checkbox">
                    <input
                      type="checkbox"
                      name="isDistributionList"
                      checked={formData.isDistributionList}
                      onChange={handleChange}
                    />
                    <span className="form-checkbox-label">
                      📧 Adicionar à lista de distribuição de e-mails
                    </span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : (editingUser ? 'Salvar' : 'Criar Usuário')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
